/* eslint-disable i18next/no-literal-string -- a code-only module: storage keys; it renders nothing */
// Text mode's unapplied text, on this device (web release 2, Part C).
//
// The textarea's text never reaches the save queue until Apply, so a route
// change, a browser Back or a reload that bypasses the editor's own Back
// would drop it. It is written here on every change and offered back the
// next time the editor opens.
//
// WRITER-QUALIFIED, like the save queue's drafts: the key carries this
// document's writer id (editorSaveQueue.tabId(), random per page load), so
// two tabs on the same deck never overwrite each other and Apply / Discard
// in one tab clears only ITS key. On open every writer's text for the deck
// is scanned and the newest one with something to apply is offered;
// restoring it moves it to this document's key (the source key goes only
// after the copy was written).
//
// Lifecycle: cleared on Apply or an explicit Discard (this writer's key, or
// the offered key on a Discard of the offer); carried to the account on a
// guest -> account migration (rekeyMigratedTextDrafts, useSavedDecks); on
// sign-out see useAuth (cleared on a clean sign-out, kept on the forced
// "unsaved edits" one, like the save queue's drafts); cleared with the
// account. Every storage touch is wrapped: Safari private mode throws on
// read as well as write.
import { ownerStorage } from '../../../lib/offline/ownerStorage'
import { tabId } from './editorSaveQueue'

const PREFIX = 'cb_editor_textdraft:'

export interface TextDraft {
  /** The editable text the edit started from. */
  base: string
  /** The text as typed. */
  text: string
  ts: number
}

/** THIS document's text draft key for the deck (or another writer's). */
export function textDraftKey(scope: string, deckId: string, writer: string = tabId()): string {
  return `${PREFIX}${scope}:${deckId}:${writer}`
}

function parse(raw: string | null): TextDraft | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<TextDraft>
    if (typeof v?.base !== 'string' || typeof v?.text !== 'string') return null
    return { base: v.base, text: v.text, ts: typeof v.ts === 'number' ? v.ts : 0 }
  } catch {
    return null
  }
}

export function readTextDraft(key: string): TextDraft | null {
  try {
    return parse(ownerStorage.getItem(key))
  } catch {
    return null
  }
}

/** Every writer's text draft for the deck, newest first. */
export function listTextDrafts(scope: string, deckId: string): { key: string; draft: TextDraft }[] {
  const out: { key: string; draft: TextDraft }[] = []
  const exact = `${PREFIX}${scope}:${deckId}`
  try {
    for (const k of ownerStorage.keys()) {
      if (!k || (k !== exact && !k.startsWith(`${exact}:`))) continue
      const draft = parse(ownerStorage.getItem(k))
      if (draft) out.push({ key: k, draft })
    }
  } catch {
    // Blocked storage holds no drafts.
  }
  return out.sort((a, b) => b.draft.ts - a.draft.ts)
}

/**
 * Resolves true only once the draft is DURABLE (localStorage at once, the
 * owner DB after its commit; r3 #1). false: refused or not committed; the
 * text lives only on screen.
 */
export async function writeTextDraft(key: string, draft: { base: string; text: string; ts?: number }): Promise<boolean> {
  try {
    const w = ownerStorage.write(key, JSON.stringify({ base: draft.base, text: draft.text, ts: draft.ts ?? Date.now() }))
    return w.accepted && await w.durable
  } catch {
    return false
  }
}

/** The stored text of a draft key exactly as it is now (the version a hand-off copies). */
export function rawTextDraft(key: string): string | null {
  try {
    return ownerStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Remove a source draft only if it still holds the exact version that was
 * copied (atomic compare-and-remove). false: it changed meanwhile (kept, to
 * be handed off again) or could not be removed.
 */
export async function clearTextDraftIfUnchanged(key: string, raw: string | null): Promise<boolean> {
  if (raw === null) return false
  try {
    return await ownerStorage.removeIfEquals(key, raw)
  } catch {
    return false
  }
}

export function clearTextDraft(key: string): void {
  try {
    ownerStorage.removeItemUnconfirmed(key)
  } catch {
    // Nothing to do.
  }
}

/** Every text draft of one scope (an account's). */
export function textDraftKeysForScope(scope: string): string[] {
  const out: string[] = []
  try {
    for (const k of ownerStorage.keys()) {
      if (k && k.startsWith(`${PREFIX}${scope}:`)) out.push(k)
    }
  } catch {
    // Storage unavailable: nothing stored either.
  }
  return out
}

/** Remove every text draft of one scope. */
export function clearTextDraftsForScope(scope: string): void {
  for (const k of textDraftKeysForScope(scope)) clearTextDraft(k)
}

/**
 * Guest -> account migration: move every guest text draft of a migrated deck
 * to the account's scope and the deck's new cloud id, keeping each writer
 * suffix. A guest key is removed only after its copy was written. false: a
 * copy could not be written (the guest key stays, and so must the guest deck).
 */
export async function rekeyMigratedTextDrafts(fromDeckId: string, toScope: string, toDeckId: string): Promise<boolean> {
  const from = `${PREFIX}guest:${fromDeckId}`
  let all = true
  for (const { key, draft } of listTextDrafts('guest', fromDeckId)) {
    const suffix = key.length > from.length ? key.slice(from.length + 1) : tabId()
    // The exact version copied: the source goes only if it still holds it.
    const raw = rawTextDraft(key)
    // The guest source goes only after the destination COMMITTED (r2 #1).
    let durable = false
    try {
      const w = ownerStorage.write(`${PREFIX}${toScope}:${toDeckId}:${suffix}`, JSON.stringify({ base: draft.base, text: draft.text, ts: draft.ts ?? Date.now() }))
      durable = w.accepted && await w.durable
    } catch {
      durable = false
    }
    if (!durable) {
      all = false
      continue
    }
    // Changed while the copy committed (r3 blocker 3): kept, and the
    // migration reports incomplete so the guest deck stays too.
    if (!(await clearTextDraftIfUnchanged(key, raw))) all = false
  }
  return all
}
